import { Answers, ConditionKey } from '@care-portals/api-interfaces';
import { femaleHRTBeluga } from './questions/HRT/flow';
import weightlossFlows from './questions/Weightloss/flows';
import acneFlows from './questions/Acne/flows';
import libidoFlows from './questions/Libido/flows';
import antiAgingFlows from './questions/Anti Aging/flows';
import PEFlows from './questions/Premature Ejaculation/flows';
import EDFlows from './questions/Erectile Dysfunction/flows';
import HairLossFlows from './questions/Hairloss/flows';
import sermorelinFlows from './questions/Sermorelin/flows';
import ecgMonitorFlows from './questions/ECG Monitior/flows';
import WeightManagementFlows from './questions/Weight Management/flows';
import MenSexHealthFlows from './questions/Mens Sexual Health/flows';
import prepFlows from './questions/PrEP/flows';
import rectalHealthFlows from './questions/RectalHealth/flows';
import bummedFlows from './questions/Bummed/flows';
import alcoholFlows from './questions/Alcohol/flows';
import microGlp1Flows from './questions/MicroDosing GLP-1/flows';
import SybilFlows from './questions/SybilHRT/flows';
import EnclomipheneFlows from './questions/Enclomiphene/flows';
import TravelKitFlows from './questions/TravelKit/flows';
import AntiNauseaFlows from './questions/AntiNausea/flows';
import ZaludFlows from './questions/Zalud/flows';
import LDNFlows from './questions/LDN/flows';
import trtFlows from './questions/TRT/flows';
import FacialAntiAgingFlows from './questions/Facial Anti Aging/flows';
import GenericFlows from './questions/Generic/flows';
import LucenceCardioHemeRISKFlows from './questions/Lucence CardioHemeRISK/flows';
import PCOSFlows from './questions/PCOS/flows';
import TestosteroneSupportEdFlows from './questions/Testosterone Support Ed/flows';
import SuboxoneFlows from './questions/Suboxone/flows';
import PeptideFlows from './questions/Peptide/flows';
import MigraineFlows from './questions/Migraine/flows';
import ModafinilFlows from './questions/Modafinil/flows';
import WeightLossCVFlows from './questions/Weight Loss CV/flows';
import MdiFlows from './questions/MDI/flows';
import { legacyForms, type LegacyFormTypeKeys } from './legacy-form-types';

type Condition = boolean | { $ne?: boolean; $gt?: number };

/** Condition keys are `${field}.${option}` — typed from FieldAnswers when T is provided */
export type ConditionQuestion<T = {}> = {
  key: keyof T extends never ? string : keyof T;
  next?: {
    key?: keyof T extends never ? string : keyof T;
    els?: keyof T extends never ? string : keyof T;
    condition?: Partial<Record<ConditionKey<T>, boolean | Condition>>;
    switch?: {
      key: keyof T extends never ? string : keyof T;
      condition?: Partial<
        Record<ConditionKey<T>, string | boolean | Condition>
      > &
      Record<string, string | boolean | Condition>;
    }[];
  };
};

export type FormTypeQuestion<T = {}> = keyof T extends never
  ? string | ConditionQuestion | (ConditionQuestion | string)[]
  : keyof T | ConditionQuestion<T> | (ConditionQuestion<T> | string)[];

export type FormType<T = {}> = {
  name: string;
  questions: FormTypeQuestion<T>[];
  onSubmit?: (answers: Answers, user?: string) => void;
  saveState?: (answers: Answers, user?: string) => any;
  /** for backwards compatibility, if this form type has older versions this will show them in the viewer UI */
  olderVersions?: string[];
  validityLength?: number;
};

type ImportedFormTypeKeys =
  | keyof typeof MdiFlows
  | keyof typeof LucenceCardioHemeRISKFlows
  | keyof typeof PCOSFlows
  | keyof typeof SuboxoneFlows
  | keyof typeof FacialAntiAgingFlows
  | keyof typeof GenericFlows
  | keyof typeof PEFlows
  | keyof typeof EDFlows
  | keyof typeof acneFlows
  | keyof typeof weightlossFlows
  | keyof typeof HairLossFlows
  | keyof typeof libidoFlows
  | keyof typeof antiAgingFlows
  | keyof typeof sermorelinFlows
  | keyof typeof rectalHealthFlows
  | keyof typeof ecgMonitorFlows
  | keyof typeof WeightManagementFlows
  | keyof typeof MenSexHealthFlows
  | keyof typeof prepFlows
  | keyof typeof bummedFlows
  | keyof typeof alcoholFlows
  | keyof typeof microGlp1Flows
  | keyof typeof SybilFlows
  | keyof typeof EnclomipheneFlows
  | keyof typeof TravelKitFlows
  | keyof typeof AntiNauseaFlows
  | keyof typeof ZaludFlows
  | keyof typeof LDNFlows
  | keyof typeof trtFlows
  | keyof typeof TestosteroneSupportEdFlows
  | keyof typeof PeptideFlows
  | keyof typeof MigraineFlows
  | keyof typeof ModafinilFlows
  | keyof typeof WeightLossCVFlows
  | 'femaleHRTBeluga';

export type FormTypeKeys = ImportedFormTypeKeys | LegacyFormTypeKeys;

export const visitTypeMap: { [key: string]: FormTypeKeys[] } = {
  weightloss: ['wlus', 'wlusOral', 'wlusBranded', 'wlusAltOnly'],
  weightlossfollowup: ['wlusmonthly', 'wlusMonthlyOral', 'wlusFollowupBranded'],
  ED: ['edus', 'edBeluga'],
  EDfollowup: ['edBelugaMonthly'],
  PE: ['peBeluga', 'peBelugaMonthly'],
  PEfollowup: ['peFollowup'],
  menopause: ['femaleHRTBeluga', 'sybilHRT'],
  hairloss: [
    'unisexHairLoss',
    'menHairLoss',
    'womenHairLoss',
    'thareHairloss',
    'hairLossFollowup',
    'hairlossPhotos'
  ],
  antiAging: ['antiAgingBeluga', 'antiAgingBelugaFollowup'],
  acne: ['acneBeluga'],
  womenSexualHealth: ['libidoBeluga'],
  metabolicSupport: ['sermorelin', 'metabolicTadalafil'],
  metabolicSupportFollowup: [
    'sermorelinFollowup',
    'metabolicTadalafilFollowup'
  ],
  constipation: ['constipation'],
  ecgMonitor: ['ecgMonitor'],
  weightManagement: ['weightMng'],
  weightManagementFollowup: ['weightMngFollowup'],
  mensSexualHealth: ['mensexhealth'],
  pruritusAni: ['pruritusAni'],
  hemorrhoids: ['hemorrhoid'],
  alcohol: ['alcohol'],
  alcoholFollowup: ['alcoholFollowup'],
  glpMicrodosing: ['microGlp1'],
  glpMicrodosingFollowup: ['microGlp1Followup'],
  testosteroneSupport: ['enclomiphene'],
  testosteroneSupportFollowup: ['enclomipheneFollowup'],
  travelKit: ['travelKit'],
  antiNausea: ['antiNausea'],
  antiNauseaFollowup: ['antiNauseaFollowup'],
  lichenSclerosus: ['lichenSclerosus'],
  ldn: ['ldn', 'dosableldn'],
  trt: ['trtEnhanced'],
  trtFollowup: ['trtFollowup', 'trtEnhancedFollowup'],
  facialAntiaging: ['facialAntiAging'],
  labAnalysis: ['lucenceCardioHemeRisk', 'lucenceInsight'],
  pcos: ['pcos'],
  pcosFollowup: ['pcosFollowup'],
  testosteroneSupportEd: ['testosteroneSupportEd'],
  suboxone: ['suboxone'],
  suboxoneFollowup: ['suboxoneFollowup'],
  peptides: ['peptide'],
  migraine: ['migraine1stLine', 'migraine2ndLine', 'migraine3rdLine'],
  modafinil: ['modafinil'],
  modafinilFollowup: ['modafinilFollowup'],
  weightlossCV: ['weightLossCv'],
};

export type EVisitType = keyof typeof visitTypeMap;

/**
 * @deprecated don't use ever, get from db instead
 */
const importedForms: Record<ImportedFormTypeKeys, FormType> = {
  ...LucenceCardioHemeRISKFlows,
  ...PCOSFlows,
  ...SuboxoneFlows,
  ...FacialAntiAgingFlows,
  femaleHRTBeluga,
  ...GenericFlows,
  ...PEFlows,
  ...EDFlows,
  ...acneFlows,
  ...weightlossFlows,
  ...HairLossFlows,
  ...libidoFlows,
  ...antiAgingFlows,
  ...sermorelinFlows,
  ...rectalHealthFlows,
  ...ecgMonitorFlows,
  ...WeightManagementFlows,
  ...MenSexHealthFlows,
  ...prepFlows,
  ...bummedFlows,
  ...alcoholFlows,
  ...microGlp1Flows,
  ...SybilFlows,
  ...EnclomipheneFlows,
  ...TravelKitFlows,
  ...AntiNauseaFlows,
  ...ZaludFlows,
  ...LDNFlows,
  ...trtFlows,
  ...TestosteroneSupportEdFlows,
  ...PeptideFlows,
  ...MigraineFlows,
  ...ModafinilFlows,
  ...WeightLossCVFlows,
  ...MdiFlows
};

const forms: Record<FormTypeKeys, FormType> = {
  ...importedForms,
  ...legacyForms
};

export const formTypes: Record<FormTypeKeys, FormType> = forms;

export default formTypes;
